/**
 * Phishing Template Editor Page
 * GitHub Issue #285
 *
 * Create and edit phishing email templates with preview functionality.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  api,
  PhishingTemplate,
  PhishingDifficulty,
} from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import {
  ArrowLeft,
  Save,
  Eye,
  X,
  AlertTriangle,
  Plus,
  Trash2,
  Code,
  Type,
  AtSign,
  User,
  Building2,
  Calendar,
  Link2,
} from 'lucide-react'

const difficultyOptions: Array<{ value: PhishingDifficulty; label: string; description: string }> = [
  { value: 'easy', label: 'Easy', description: 'Obvious phishing indicators' },
  { value: 'medium', label: 'Medium', description: 'Some subtle indicators' },
  { value: 'hard', label: 'Hard', description: 'Difficult to detect' },
  { value: 'expert', label: 'Expert', description: 'Nearly indistinguishable from legitimate' },
]

const categoryOptions = [
  'Credential Harvesting',
  'Malware Delivery',
  'Invoice/Payment',
  'IT Support',
  'HR/Benefits',
  'Executive Impersonation',
  'Social Media',
  'Package Delivery',
  'Account Security',
  'Other',
]

// Template variables that can be inserted
const templateVariables = [
  { name: '{{first_name}}', description: 'Recipient first name', icon: User },
  { name: '{{last_name}}', description: 'Recipient last name', icon: User },
  { name: '{{email}}', description: 'Recipient email', icon: AtSign },
  { name: '{{department}}', description: 'Recipient department', icon: Building2 },
  { name: '{{company}}', description: 'Company name', icon: Building2 },
  { name: '{{current_date}}', description: 'Current date', icon: Calendar },
  { name: '{{phishing_link}}', description: 'Tracking link', icon: Link2 },
]

export default function PhishingTemplateEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const confirmDialog = useConfirm()

  const isNew = id === 'new'

  // State
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [showHtmlEditor, setShowHtmlEditor] = useState(false)

  // Form data
  const [form, setForm] = useState({
    name: '',
    subject: '',
    body_html: '',
    body_text: '',
    sender_name: '',
    sender_email: '',
    category: 'Other',
    difficulty: 'medium' as PhishingDifficulty,
    indicators: [] as string[],
    landing_page_html: '',
    capture_credentials: false,
  })

  const [newIndicator, setNewIndicator] = useState('')

  // Fetch template if editing
  const fetchTemplate = useCallback(async () => {
    if (isNew || !id) return
    setLoading(true)
    try {
      const response = await api.getPhishingTemplate(id)
      if (response.success) {
        const template = response.data
        setForm({
          name: template.name,
          subject: template.subject,
          body_html: template.body_html,
          body_text: template.body_text || '',
          sender_name: template.sender_name,
          sender_email: template.sender_email,
          category: template.category,
          difficulty: template.difficulty,
          indicators: template.indicators || [],
          landing_page_html: template.landing_page_html || '',
          capture_credentials: template.capture_credentials,
        })
      }
    } catch (error) {
      console.error('Failed to fetch template:', error)
      toast.error('Failed to load template')
    } finally {
      setLoading(false)
    }
  }, [id, isNew, toast])

  useEffect(() => {
    fetchTemplate()
  }, [fetchTemplate])

  // Save template
  const handleSave = async () => {
    if (!form.name || !form.subject || !form.body_html || !form.sender_name || !form.sender_email) {
      toast.error('Please fill in all required fields')
      return
    }

    setSaving(true)
    try {
      if (isNew) {
        const response = await api.createPhishingTemplate(form)
        if (response.success) {
          toast.success('Template created')
          navigate(`/phishing/templates/${response.data.id}`)
        }
      } else if (id) {
        const response = await api.updatePhishingTemplate(id, form)
        if (response.success) {
          toast.success('Template saved')
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  // Preview template
  const handlePreview = async () => {
    try {
      if (isNew || !id) {
        // For new templates, just show the raw HTML
        setPreviewHtml(form.body_html)
      } else {
        const response = await api.previewPhishingTemplate(id, {
          first_name: 'John',
          last_name: 'Doe',
          email: 'john.doe@company.com',
          department: 'Engineering',
          company: 'ACME Corp',
        })
        if (response.success) {
          setPreviewHtml(response.html)
        }
      }
      setShowPreview(true)
    } catch (error: any) {
      // Fallback to raw HTML
      setPreviewHtml(form.body_html)
      setShowPreview(true)
    }
  }

  // Insert variable at cursor position
  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('body-html-input') as HTMLTextAreaElement
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = form.body_html
      const before = text.substring(0, start)
      const after = text.substring(end)
      setForm({ ...form, body_html: before + variable + after })
      // Restore focus and cursor position
      setTimeout(() => {
        textarea.focus()
        textarea.selectionStart = textarea.selectionEnd = start + variable.length
      }, 0)
    }
  }

  // Add indicator
  const addIndicator = () => {
    if (newIndicator.trim() && !form.indicators.includes(newIndicator.trim())) {
      setForm({ ...form, indicators: [...form.indicators, newIndicator.trim()] })
      setNewIndicator('')
    }
  }

  // Remove indicator
  const removeIndicator = (index: number) => {
    setForm({ ...form, indicators: form.indicators.filter((_, i) => i !== index) })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/phishing')}
            className="btn btn-secondary"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNew ? 'Create Phishing Template' : 'Edit Phishing Template'}
            </h1>
            <p className="text-gray-500 mt-1">Design a phishing email template for security testing</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handlePreview}
            className="btn btn-secondary"
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Template
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Basic Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input w-full"
                placeholder="e.g., IT Password Reset Request"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="input w-full"
                >
                  {categoryOptions.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select
                  value={form.difficulty}
                  onChange={(e) => setForm({ ...form, difficulty: e.target.value as PhishingDifficulty })}
                  className="input w-full"
                >
                  {difficultyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label} - {opt.description}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Sender Info */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Sender Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.sender_name}
                  onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., IT Support Team"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.sender_email}
                  onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., it-support@company.com"
                />
              </div>
            </div>
          </div>

          {/* Email Content */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Email Content</h3>
              <button
                onClick={() => setShowHtmlEditor(!showHtmlEditor)}
                className="btn btn-sm btn-secondary"
              >
                {showHtmlEditor ? <Type className="h-4 w-4 mr-1" /> : <Code className="h-4 w-4 mr-1" />}
                {showHtmlEditor ? 'Visual' : 'HTML'}
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject Line <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="input w-full"
                placeholder="e.g., Action Required: Password Reset"
              />
            </div>

            {/* Variable Toolbar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Insert Variable</label>
              <div className="flex flex-wrap gap-2">
                {templateVariables.map((v) => (
                  <button
                    key={v.name}
                    onClick={() => insertVariable(v.name)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border transition-colors"
                    title={v.description}
                  >
                    <v.icon className="h-3 w-3" />
                    {v.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Body (HTML) <span className="text-red-500">*</span>
              </label>
              <textarea
                id="body-html-input"
                value={form.body_html}
                onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                className="input w-full font-mono text-sm"
                rows={12}
                placeholder={`<p>Hello {{first_name}},</p>
<p>Your password will expire in 24 hours. Please click the link below to reset it:</p>
<p><a href="{{phishing_link}}">Reset Password</a></p>
<p>Thanks,<br>IT Support</p>`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Use HTML tags for formatting. Include {"{{phishing_link}}"} where you want the tracking link.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plain Text Version
              </label>
              <textarea
                value={form.body_text}
                onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                className="input w-full font-mono text-sm"
                rows={6}
                placeholder="Plain text version of the email (optional)"
              />
            </div>
          </div>

          {/* Landing Page */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Landing Page (Optional)</h3>
            <p className="text-sm text-gray-500">
              Create a fake login page that users will see when they click the phishing link.
            </p>

            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={form.capture_credentials}
                  onChange={(e) => setForm({ ...form, capture_credentials: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Capture submitted credentials</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Landing Page HTML
              </label>
              <textarea
                value={form.landing_page_html}
                onChange={(e) => setForm({ ...form, landing_page_html: e.target.value })}
                className="input w-full font-mono text-sm"
                rows={8}
                placeholder="HTML for the landing page (login form, etc.)"
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Phishing Indicators */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Phishing Indicators</h3>
            <p className="text-sm text-gray-500 mb-4">
              List the red flags that users should notice in this email. These are shown during training.
            </p>

            <div className="space-y-3">
              {form.indicators.map((indicator, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-2 bg-yellow-50 rounded border border-yellow-200"
                >
                  <span className="text-sm text-yellow-800">{indicator}</span>
                  <button
                    onClick={() => removeIndicator(idx)}
                    className="text-yellow-600 hover:text-yellow-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <input
                type="text"
                value={newIndicator}
                onChange={(e) => setNewIndicator(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addIndicator())}
                className="input flex-1"
                placeholder="e.g., Urgency language"
              />
              <button
                onClick={addIndicator}
                className="btn btn-secondary"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            {/* Suggested indicators based on difficulty */}
            {form.indicators.length === 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500 mb-2">Suggested indicators:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>- Urgency or threatening language</li>
                  <li>- Suspicious sender email domain</li>
                  <li>- Generic greeting (no name)</li>
                  <li>- Grammar/spelling errors</li>
                  <li>- Suspicious link URL</li>
                  <li>- Request for sensitive information</li>
                </ul>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Template Tips
            </h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>- Match difficulty to your team's experience level</li>
              <li>- Include {"{{phishing_link}}"} in the body for tracking</li>
              <li>- Use realistic sender addresses</li>
              <li>- Test the preview before launching campaigns</li>
              <li>- Add indicators to help with training</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Email Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-1">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">From:</span> {form.sender_name} &lt;{form.sender_email}&gt;
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Subject:</span> {form.subject}
                </p>
              </div>

              <div
                className="prose max-w-none border rounded-lg p-4"
                dangerouslySetInnerHTML={{ __html: previewHtml || form.body_html }}
              />

              {form.indicators.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800 mb-2">Phishing Indicators:</p>
                  <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                    {form.indicators.map((indicator, idx) => (
                      <li key={idx}>{indicator}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowPreview(false)}
                className="btn btn-secondary w-full"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
